import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { t } from "ttag";
import { IBannerState } from "./LibraryActionBanner";
import { buildExportFileName, dedupeImportedName } from "./libraryNaming";

interface INamedItem {
  id?: string;
  name: string;
}

export interface ILibrarySaveApi {
  isDirty: boolean;
  saving: boolean;
  save: () => void;
}

interface IUseLibraryTabConfig<T extends INamedItem> {
  items: T[];
  /** Reload the list. Awaited after saving, so a promise-returning refetch is honored. */
  refetch: () => void | Promise<any>;
  /**
   * Whether this tab is the one currently on screen. The underlying query only
   * fetches once (see useQuery), so a rename/delete made elsewhere (e.g. saving
   * a device to the library from DeviceMapper) while this tab sat inactive
   * would otherwise never show up here — refetch whenever it becomes active again.
   */
  active: boolean;
  siteId?: string;
  /** Create one item from an imported JSON entry (site is merged in automatically). */
  create: (item: any) => Promise<any>;
  rename: (templateId: string, name: string) => Promise<any>;
  remove: (templateId: string) => Promise<any>;
  /** Which fields of an item to keep in the exported JSON. */
  pickExportFields: (item: T) => Record<string, any>;
  /**
   * What one imported record is called, for the import success message —
   * "devices", "units", "control". Plural; the message always reads "xx new …".
   */
  importedItemsLabel: string;
  /**
   * Notifies the dialog of this tab's current save affordance, so the dialog's
   * own footer button can show/drive it (and warn before switching tabs with
   * staged edits). Called with null on unmount so a tab switch clears it.
   */
  onSaveApiChange?: (api: ILibrarySaveApi | null) => void;
}

/**
 * All the state/handlers shared by the three Site Library tabs (Devices,
 * Functional Units, Control UI): search, checkbox-select-to-export, JSON
 * download, JSON file import with progress, and staged rename/delete behind an
 * explicit Save. Each tab only differs in its data source and table columns.
 *
 * Renames and deletes are *staged*, not applied as they happen.
 */
export const useLibraryTab = <T extends INamedItem>(config: IUseLibraryTabConfig<T>) => {
  const [search, setSearch] = useState("");
  const [exportMode, setExportMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [importProgress, setImportProgress] = useState<{ done: number; total: number } | null>(null);
  const [banner, setBanner] = useState<IBannerState | null>(null);
  const [editingNames, setEditingNames] = useState<Record<string, string>>({});
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const byId = useMemo(() => {
    const map = new Map<string, T>();
    config.items.forEach((item) => item.id && map.set(item.id, item));
    return map;
  }, [config.items]);

  /**
   * Only names that actually differ from what is stored.
   */
  const changedNames = useMemo(() => {
    const result: Record<string, string> = {};
    Object.keys(editingNames).forEach((id) => {
      const draft = editingNames[id].trim();
      const stored = byId.get(id)?.name;
      if (draft && stored !== undefined && draft !== stored) {
        result[id] = draft;
      }
    });
    return result;
  }, [editingNames, byId]);

  const renameCount = Object.keys(changedNames).length;
  const deleteCount = pendingDeleteIds.size;
  const isDirty = renameCount > 0 || deleteCount > 0;

  // Refetch whenever this tab goes from inactive to active (e.g. the Library
  // dialog is closed and reopened on the same tab) — not on the initial mount,
  // which the query already fetches on its own.
  const wasActiveRef = useRef(config.active);
  useEffect(() => {
    if (config.active && !wasActiveRef.current) {
      config.refetch();
    }
    wasActiveRef.current = config.active;
  }, [config.active]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * A success banner clears on the next click anywhere.
   */
  useEffect(() => {
    if (banner?.type !== "success") {
      return undefined;
    }
    let dismiss: (() => void) | undefined;
    const timer = setTimeout(() => {
      dismiss = () => setBanner((current) => (current?.type === "success" ? null : current));
      document.addEventListener("click", dismiss, { once: true, capture: true });
    }, 0);

    return () => {
      clearTimeout(timer);
      if (dismiss) {
        document.removeEventListener("click", dismiss, { capture: true });
      }
    };
  }, [banner]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matched = q ? config.items.filter((item) => item.name.toLowerCase().includes(q)) : config.items;
    // Sorted a-z by name. numeric:true so "Device 2" precedes "Device 10"
    // instead of sorting lexicographically.
    return [...matched].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, { numeric: true, sensitivity: "base" }),
    );
  }, [config.items, search]);

  const toggleSelected = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const exitExportMode = () => {
    setExportMode(false);
    setSelectedIds(new Set());
  };

  const handleExportSelected = () => {
    const toExport = config.items.filter((item) => item.id && selectedIds.has(item.id)).map(config.pickExportFields);
    if (!toExport.length) {
      return;
    }
    const blob = new Blob([JSON.stringify(toExport, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${buildExportFileName()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    const count = toExport.length;
    exitExportMode();
    setBanner({ type: "success", message: count === 1 ? t`File exported successfully.` : t`${count} selected items exported successfully.` });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !config.siteId) return;
    e.target.value = "";
    setBanner(null);

    const text = await file.text();
    let items: any[];
    try {
      const parsed = JSON.parse(text);
      items = Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      setBanner({ type: "error", message: t`Could not import file. Upload a valid .json file.` });
      return;
    }

    // Seeded with the names already in the library, then grown as each record is
    // accepted, so an imported name never lands on top of an existing row or on
    // an earlier record from the same file.
    const takenNames = new Set(config.items.map((item) => (item.name ?? "").trim().toLowerCase()));

    setImportProgress({ done: 0, total: items.length });
    let failed = 0;
    let imported = 0;
    for (let i = 0; i < items.length; i++) {
      const name = dedupeImportedName(items[i]?.name ?? "", takenNames);
      const res = await config.create({ ...items[i], name, site: config.siteId });
      if (res) {
        imported++;
        if (name) takenNames.add(name.toLowerCase());
      } else {
        failed++;
      }
      setImportProgress({ done: i + 1, total: items.length });
    }
    setImportProgress(null);
    config.refetch();
    if (failed === 0) {
      setBanner({
        type: "success",
        message: t`Import Modbus library file completed. ${imported} new ${config.importedItemsLabel} imported`,
      });
    } else if (failed === items.length) {
      // Nothing landed — same copy as an unparseable file, since from the
      // user's side the whole import failed either way.
      setBanner({ type: "error", message: t`Could not import file. Upload a valid .json file.` });
    } else {
      setBanner({ type: "error", message: t`${failed} of ${items.length} items could not be imported.` });
    }
  };

  const setDraftName = (id: string, value: string) => setEditingNames((prev) => ({ ...prev, [id]: value }));

  /** Marks a row for deletion, or takes it back off the list. */
  const togglePendingDelete = (id: string) =>
    setPendingDeleteIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const discardChanges = useCallback(() => {
    setEditingNames({});
    setPendingDeleteIds(new Set());
  }, []);

  /**
   * Applies the staged edits: deletions first, then renames.
   *
   */
  const handleSave = async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    setBanner(null);

    const deleteIds = Array.from(pendingDeleteIds);
    const renameIds = Object.keys(changedNames).filter((id) => !pendingDeleteIds.has(id));

    let failed = 0;
    let deleted = 0;
    let renamed = 0;

    for (const id of deleteIds) {
      const res = await config.remove(id);
      res ? deleted++ : failed++;
    }
    for (const id of renameIds) {
      const res = await config.rename(id, changedNames[id]);
      res ? renamed++ : failed++;
    }

    await config.refetch();
    setSaving(false);

    if (failed === 0) {
      // Only clear the staged edits once everything landed
      discardChanges();
      const parts: string[] = [];
      if (renamed) parts.push(t`${renamed} renamed`);
      if (deleted) parts.push(t`${deleted} deleted`);
      setBanner({ type: "success", message: t`Changes saved.` + (parts.length ? ` ${parts.join(", ")}.` : "") });
    } else {
      setBanner({ type: "error", message: t`${failed} of ${deleteIds.length + renameIds.length} changes could not be saved.` });
    }
  };

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    config.onSaveApiChange?.({ isDirty, saving, save: () => handleSaveRef.current() });
  }, [isDirty, saving]);

  useEffect(() => () => config.onSaveApiChange?.(null), []);

  const filteredIds = filtered.map((r) => r.id).filter(Boolean) as string[];
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filteredIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => new Set([...prev, ...filteredIds]));
    }
  };

  return {
    search,
    setSearch,
    exportMode,
    setExportMode,
    selectedIds,
    exitExportMode,
    handleExportSelected,
    importing: importProgress !== null,
    importProgress,
    fileInputRef,
    handleFileChange,
    banner,
    filtered,
    editingNames,
    setDraftName,
    pendingDeleteIds,
    togglePendingDelete,
    isDirty,
    saving,
    handleSave,
    discardChanges,
    allSelected,
    toggleAll,
    toggleSelected,
  };
};

export type ILibraryTab<T extends INamedItem> = ReturnType<typeof useLibraryTab<T>>;
